import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshCardStyles } from '../utils/refreshAnimation';
import { agentService } from '../services/agentService';
import { metricsService } from '../services/metricsService';
import { filterDiskUsageByAgentThresholds } from '../utils/diskUtils';
import type { Agent } from '../types/agent';
import { AgentCapability, capabilityHelper } from '../types/agent';
import type { CurrentMetrics, DiskUsage } from '../types/metrics';
import { formatDateToTR } from '../utils/dateUtils';

const parseDiskUsage = (diskUsageJson: string | null | undefined): DiskUsage => {
  if (diskUsageJson == null || diskUsageJson === '') return {};
  try {
    const parsed = JSON.parse(diskUsageJson);
    if (parsed == null) return {};
    if (Array.isArray(parsed)) {
      const result: DiskUsage = {};
      parsed.forEach((item: { Drive?: string; Device?: string; UsagePercent?: number }) => {
        const driveName = item.Drive || item.Device;
        if (driveName && item.UsagePercent !== undefined) {
          result[driveName] =
            typeof item.UsagePercent === 'number' ? item.UsagePercent : parseFloat(String(item.UsagePercent)) || 0;
        }
      });
      return result;
    }
    if (typeof parsed === 'object' && parsed !== null) {
      const result: DiskUsage = {};
      Object.entries(parsed).forEach(([key, value]) => {
        result[key] = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      });
      return result;
    }
    return {};
  } catch {
    return {};
  }
};

type AgentActiveFilter = 'active' | 'inactive' | 'all';

const Agents = () => {
  const { selectedTenantIds, selectedServerIds, refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [activeFilter, setActiveFilter] = useState<AgentActiveFilter>('active');
  const [currentMetrics, setCurrentMetrics] = useState<Record<string, CurrentMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [copiedAgentId, setCopiedAgentId] = useState<string | null>(null);
  const navigate = useNavigate();

  const filteredAgents = agents.filter((a) => {
    if (activeFilter === 'active') return a.isActive === true;
    if (activeFilter === 'inactive') return a.isActive === false;
    return true;
  });

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;
      const selectedServerId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;
      const data = await agentService.getAll(selectedServerId, selectedTenantId);
      setAgents(data);
      return data;
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
        ? (err as { response: { data: { message: string } } }).response.data.message
        : "Agent'lar yüklenirken bir hata oluştu";
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedTenantIds, selectedServerIds]);

  const loadMetrics = useCallback(async (agentList: Agent[]) => {
    const serverIds = Array.from(new Set(agentList.map((a) => a.serverId)));
    const metrics: Record<string, CurrentMetrics> = {};
    await Promise.all(
      serverIds.map(async (serverId) => {
        try {
          const metric = await metricsService.getCurrentMetrics(serverId);
          if (metric) metrics[serverId] = metric;
        } catch {
          // Metrik yoksa atla
        }
      })
    );
    setCurrentMetrics(metrics);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const data = await loadAgents();
      if (!cancelled && data.length > 0) {
        await loadMetrics(data);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loadAgents, loadMetrics, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh || agents.length === 0) return;
    const interval = setInterval(() => {
      loadAgents().then((data) => {
        if (data.length > 0) loadMetrics(data);
      });
    }, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, agents.length, loadAgents, loadMetrics]);

  const handleCreate = () => {
    navigate('/agents/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/agents/${id}/edit`);
  };

  const handleDeleteClick = (agent: Agent) => {
    setSelectedAgent(agent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAgent) return;
    try {
      await agentService.delete(selectedAgent.id);
      await loadAgents().then((data) => {
        if (data.length > 0) loadMetrics(data);
      });
      setDeleteDialogOpen(false);
      setSelectedAgent(null);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
        ? (err as { response: { data: { message: string } } }).response.data.message
        : 'Agent silinirken bir hata oluştu';
      alert(message);
    }
  };

  const handleCopyAgentId = async (agentId: string) => {
    try {
      await navigator.clipboard.writeText(agentId);
      setCopiedAgentId(agentId);
      setTimeout(() => setCopiedAgentId(null), 2000);
    } catch {
      // Kopyalama hatası
    }
  };

  const handleIncele = (serverId: string) => {
    navigate(`/metrics/servers/${serverId}`);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <Typography variant="h4" component="h1">
            Agent'lar
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="agent-active-filter-label">Durum</InputLabel>
              <Select
                labelId="agent-active-filter-label"
                value={activeFilter}
                label="Durum"
                onChange={(e) => setActiveFilter(e.target.value as AgentActiveFilter)}
              >
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Pasif</MenuItem>
                <MenuItem value="all">Tümü</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              Yeni Agent
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {agents.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Henüz agent bulunmuyor.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={{ mt: 2 }}>
              İlk Agent'ı Oluştur
            </Button>
          </Paper>
        ) : filteredAgents.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Seçilen filtreye uygun agent bulunmuyor.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
              gap: 2,
            }}
          >
            {filteredAgents.map((agent) => {
              const metrics = currentMetrics[agent.serverId];
              const isAgentActive = agent.isActive;
              const isPassive = !isAgentActive;
              const hasOnlineAgent = isAgentActive && agent.isOnline;
              const isUnreachable = !hasOnlineAgent && isAgentActive;
              const hasCpuCap = capabilityHelper.hasCapability(agent.capabilities, AgentCapability.CpuMonitoring);
              const hasRamCap = capabilityHelper.hasCapability(agent.capabilities, AgentCapability.MemoryMonitoring);
              const hasDiskCap = capabilityHelper.hasCapability(agent.capabilities, AgentCapability.DiskMonitoring);
              const cpuWarningThreshold = agent.cpuWarningThreshold ?? 80;
              const cpuCriticalThreshold = agent.cpuCriticalThreshold ?? 90;
              const ramWarningThreshold = agent.ramWarningThreshold ?? 80;
              const ramCriticalThreshold = agent.ramCriticalThreshold ?? 90;
              const cpuExceedsCritical = hasCpuCap && metrics && metrics.latestCpuUsage >= cpuCriticalThreshold;
              const cpuExceedsWarning =
                hasCpuCap && metrics && metrics.latestCpuUsage >= cpuWarningThreshold && !cpuExceedsCritical;
              const ramExceedsCritical = hasRamCap && metrics && metrics.latestRamUsagePercent >= ramCriticalThreshold;
              const ramExceedsWarning =
                hasRamCap && metrics && metrics.latestRamUsagePercent >= ramWarningThreshold && !ramExceedsCritical;

              return (
                <Card
                  key={agent.id}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderLeft: '4px solid',
                    borderColor: 'divider',
                    borderLeftColor:
                      isPassive ? 'grey.400' : !hasOnlineAgent && isAgentActive ? 'error.main' : hasOnlineAgent ? 'success.main' : 'grey.400',
                    overflow: 'hidden',
                    position: 'relative',
                    opacity: isPassive || isUnreachable ? 0.65 : 1,
                    backgroundColor: isPassive || isUnreachable ? 'grey.50' : 'background.paper',
                    boxShadow: hasOnlineAgent
                      ? '0 2px 12px rgba(0,0,0,0.06), -4px 0 12px -2px rgba(16,185,129,0.7)'
                      : '0 2px 12px rgba(0,0,0,0.06)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: isPassive || isUnreachable ? 'none' : 'translateY(-3px)',
                      boxShadow: isPassive || isUnreachable ? 'none' : '0 12px 28px -8px rgba(0,0,0,0.12)',
                    },
                    ...getRefreshCardStyles(isRefreshing),
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.25} mb={2}>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: isPassive || isUnreachable ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          {agent.serverName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={isPassive || isUnreachable ? 'text.disabled' : 'text.secondary'}
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {agent.tenantName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.65rem',
                              wordBreak: 'break-all',
                              overflow: 'hidden',
                              maxWidth: '100%',
                            }}
                            title={agent.id}
                          >
                            {agent.id}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyAgentId(agent.id);
                            }}
                            color={copiedAgentId === agent.id ? 'success' : 'default'}
                            aria-label="Agent ID kopyala"
                            sx={{ p: 0.25, flexShrink: 0 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Chip
                          label={isPassive ? 'Pasif' : agent.isOnline ? 'Online' : agent.lastHeartbeat ? 'Erişim Yok' : 'Bilinmiyor'}
                          color={isPassive ? 'default' : agent.isOnline ? 'success' : agent.lastHeartbeat ? 'error' : 'default'}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                        />
                        <Tooltip title="Metrik detayı (sunucu metrik ekranı)">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleIncele(agent.serverId);
                            }}
                            color="primary"
                            aria-label="İncele"
                            sx={{ p: 0.35 }}
                          >
                            <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(agent.id);
                          }}
                          color="primary"
                          aria-label="Düzenle"
                          sx={{ p: 0.35 }}
                        >
                          <EditIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(agent);
                          }}
                          color="error"
                          aria-label="Sil"
                          sx={{ p: 0.35 }}
                        >
                          <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    {metrics && (hasCpuCap || hasRamCap || hasDiskCap) && (
                      <>
                        {(hasCpuCap || hasRamCap) && (
                          <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
                            {hasCpuCap && (
                              <Box
                                sx={{
                                  flex: '1 1 0',
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1.25,
                                  borderRadius: 2,
                                  bgcolor: 'grey.50',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <CircularProgress
                                    variant="determinate"
                                    value={Math.min(100, metrics.latestCpuUsage)}
                                    size={44}
                                    thickness={4}
                                    sx={{
                                      color: cpuExceedsCritical ? 'error.main' : cpuExceedsWarning ? 'warning.main' : 'success.main',
                                      '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      sx={{
                                        fontSize: '0.65rem',
                                        color: cpuExceedsCritical ? 'error.main' : cpuExceedsWarning ? 'warning.main' : 'success.main',
                                      }}
                                    >
                                      {metrics.latestCpuUsage.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  CPU
                                </Typography>
                              </Box>
                            )}
                            {hasRamCap && (
                              <Box
                                sx={{
                                  flex: '1 1 0',
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1.25,
                                  borderRadius: 2,
                                  bgcolor: 'grey.50',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <CircularProgress
                                    variant="determinate"
                                    value={Math.min(100, metrics.latestRamUsagePercent)}
                                    size={44}
                                    thickness={4}
                                    sx={{
                                      color: ramExceedsCritical ? 'error.main' : ramExceedsWarning ? 'warning.main' : 'success.main',
                                      '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      sx={{
                                        fontSize: '0.65rem',
                                        color: ramExceedsCritical ? 'error.main' : ramExceedsWarning ? 'warning.main' : 'success.main',
                                      }}
                                    >
                                      {metrics.latestRamUsagePercent.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  RAM
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {hasDiskCap && metrics.latestDiskUsage && (() => {
                          const diskUsageRaw = parseDiskUsage(metrics.latestDiskUsage);
                          const diskUsage = filterDiskUsageByAgentThresholds(diskUsageRaw, agent.diskThresholds);
                          const diskEntries = Object.entries(diskUsage)
                            .map(([drive, usage]) => ({ drive, usageNum: typeof usage === 'number' ? usage : parseFloat(String(usage)) || 0 }))
                            .sort((a, b) => b.usageNum - a.usageNum);
                          if (diskEntries.length === 0) return null;
                          let diskThresholds: Array<{ drive: string; warning: number; critical: number }> | null = null;
                          try {
                            if (agent.diskThresholds) diskThresholds = JSON.parse(agent.diskThresholds);
                          } catch {
                            /* ignore */
                          }
                          const getDiskColor = (usageNum: number, drive: string) => {
                            const normalized = drive.trim().toUpperCase().endsWith(':') ? drive.trim().toUpperCase() : drive.trim().toUpperCase() + ':';
                            let w = 80,
                              c = 90;
                            if (diskThresholds?.length) {
                              const cfg = diskThresholds.find((d: { drive?: string }) => {
                                const n = (d.drive?.trim().toUpperCase() || '').endsWith(':') ? d.drive?.trim().toUpperCase() : (d.drive?.trim().toUpperCase() || '') + ':';
                                return n === normalized;
                              });
                              if (cfg) {
                                w = cfg.warning ?? 80;
                                c = cfg.critical ?? 90;
                              }
                            }
                            if (usageNum >= c) return 'error.main';
                            if (usageNum >= w) return 'warning.main';
                            return 'success.main';
                          };
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {diskEntries.slice(0, 4).map(({ drive, usageNum }) => {
                                const diskColor = getDiskColor(usageNum, drive);
                                return (
                                  <Box
                                    key={drive}
                                    sx={{
                                      flex: '1 1 0',
                                      minWidth: '60px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      p: 1,
                                      borderRadius: 2,
                                      bgcolor: 'grey.50',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                                      <CircularProgress
                                        variant="determinate"
                                        value={Math.min(100, usageNum)}
                                        size={36}
                                        thickness={4}
                                        sx={{ color: diskColor, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }}
                                      />
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.6rem', color: diskColor }}>
                                          {usageNum.toFixed(0)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={drive}>
                                      {drive}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })()}
                      </>
                    )}

                    {(!metrics || (!hasCpuCap && !hasRamCap && !hasDiskCap)) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                        {!metrics ? 'Metrik yok' : 'Metrik özelliği kapalı'}
                      </Typography>
                    )}

                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        v{agent.agentVersion} · {agent.osTypeName} · Son erişim: {agent.lastHeartbeat ? formatDateToTR(agent.lastHeartbeat) : '—'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Agent Sil</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bu agent'ı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Sil
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Agents;
